/**
 * 用户相关接口
 */

import { requestPostMethod, requestGetMethod, resultHandler } from './base';
import axios from 'axios';

// 基础URL常量
const BASE_URL = 'https://1167.mk-xy.com';

/**
 * 发送验证码
 * @param mobile 手机号
 * @returns
 */
export const sendVerificationCode = async (mobile: string) => {
  const request = requestPostMethod('/api/index/send', {
    baseUrl: BASE_URL,
  });

  try {
    const response = await request({
      mobile,
      event: 'mobilelogin',
    });
    const result = await response.json();
    return resultHandler(result);
  } catch (error) {
    console.error('发送验证码失败:', error);
    throw error;
  }
};

/**
 * 手机号登录
 * @param mobile 手机号
 * @param captcha 验证码
 * @returns 用户信息和token
 */
export const mobileLogin = async (mobile: string, captcha: string) => {
  const request = requestPostMethod('/api/index/mobileLogin', {
    baseUrl: BASE_URL,
  });

  try {
    const response = await request({
      mobile,
      captcha,
    });
    const result = await response.json();

    // 如果登录成功，保存token到localStorage
    if (result.code === 200 && result.data?.userinfo?.token) {
      localStorage.setItem('token', result.data.userinfo.token);
      localStorage.setItem('userInfo', JSON.stringify(result.data.userinfo));
    }

    return result;
  } catch (error) {
    console.error('手机号登录失败:', error);
    throw error;
  }
};

/**
 * 获取应用配置
 * @returns 应用配置信息，包括logo和描述
 */
export const getAppConfig = async () => {
  const request = requestGetMethod('/api/index/config', {
    baseUrl: BASE_URL,
  });

  try {
    const response = await request();
    const result = await response.json();

    if (result.code === 200) {
      return result.data;
    } else {
      console.error('获取配置失败:', result.msg);
      return { logo: '', desc: '' };
    }
  } catch (error) {
    console.error('获取配置信息失败:', error);
    // 返回默认值
    return { logo: '', desc: '' };
  }
};

/**
 * 获取用户信息
 * @returns 用户详细信息
 */
export const getUserInfo = async () => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestGetMethod('/api/index/userInfo', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request();
    const result = await response.json();

    console.log('获取到的用户信息: ', JSON.stringify(result));

    if (result.code === 200) {
      // 更新本地存储的用户信息
      localStorage.setItem('userInfo', JSON.stringify(result.data));
      return result.data;
    } else {
      console.error('获取用户信息失败:', result.msg);
      throw new Error(result.msg || '获取用户信息失败');
    }
  } catch (error) {
    console.error('获取用户信息请求失败:', error);
    throw error;
  }
};

/**
 * 上传文件 - 使用axios实现
 * @param file 要上传的文件
 * @returns 上传后的文件URL信息
 */
export const uploadFile = async (file: File) => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  // 创建FormData对象
  const formData = new FormData();
  formData.append('file', file);

  try {
    // 使用axios发送请求
    const response = await axios.post(`${BASE_URL}/api/index/upload`, formData, {
      headers: {
        token: token || '',
        // 当使用FormData时，axios会自动设置content-type为multipart/form-data并添加boundary
      },
    });

    // axios自动将响应解析为JSON
    const result = response.data;

    if (result.code === 200) {
      return result.data;
    } else {
      console.error('文件上传失败:', result.msg);
      throw new Error(result.msg || '文件上传失败');
    }
  } catch (error) {
    // axios错误处理
    if (axios.isAxiosError(error) && error.response) {
      console.error('文件上传请求失败:', error.response.data);
      throw new Error(error.response.data?.msg || '文件上传请求失败');
    } else {
      console.error('文件上传请求失败:', error);
      throw error;
    }
  }
};

/**
 * 退出登录
 * @returns 退出登录结果
 */
export const logout = async () => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    console.warn('未登录或token不存在，无需调用退出接口');
    // 即使没有token也清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    return { code: 200, msg: '已退出登录' };
  }

  const request = requestPostMethod('/api/index/logout', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request({});
    const result = await response.json();

    // 无论接口调用成功与否，都清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');

    return result;
  } catch (error) {
    console.error('退出登录请求失败:', error);
    // 出错时也清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    throw error;
  }
};

/**
 * 获取角色列表
 * @returns 用户可用的角色列表
 */
export const getPersonaList = async () => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestGetMethod('/api/index/personaList', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request();
    const result = await response.json();

    if (result.code === 200) {
      return result.data;
    } else {
      console.error('获取角色列表失败:', result.msg);
      throw new Error(result.msg || '获取角色列表失败');
    }
  } catch (error) {
    console.error('获取角色列表请求失败:', error);
    throw error;
  }
};

/**
 * 生成AI通话token
 * @returns 包含room_id, token和user_id的对象
 */
export const createAiToken = async () => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestPostMethod('/api/index/aiTokenCreate', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request({});
    const result = await response.json();

    if (result.code === 200) {
      return result.data;
    } else {
      console.error('生成AI通话token失败:', result.msg);
      throw new Error(result.msg || '生成AI通话token失败');
    }
  } catch (error) {
    console.error('生成AI通话token请求失败:', error);
    throw error;
  }
};

/**
 * 创建AI对话
 * @param params 对话参数
 * @returns 创建对话结果
 */
export const createAiTalk = async (params: {
  persona_id: number | string; // 角色id
  room_id: string; // 房间id
  token: string; // token
  value: string; // 内容
  time: string | number; // 时间
  user: string; // 用户
  paragraph: string | boolean; // 段落
  definite: string | boolean; // 是否明确
  isInterrupted: string | boolean; // 是否中断
}) => {
  // 从localStorage获取认证token
  const authToken = localStorage.getItem('token');

  if (!authToken) {
    throw new Error('未登录或token不存在');
  }

  const request = requestPostMethod('/api/index/aiTalkCreate', {
    baseUrl: BASE_URL,
    headers: {
      token: authToken,
    },
  });

  try {
    const response = await request(params);
    const result = await response.json();
    return result.data;
    // if (result.code === 200) {
    //
    // } else {
    //   console.error('创建AI对话失败:', result.msg);
    //   throw new Error(result.msg || '创建AI对话失败');
    // }
  } catch (error) {
    console.error('创建AI对话请求失败:', error);
    throw error;
  }
};

/**
 * 获取消息记录列表
 * @param personaId 角色ID
 * @returns 指定角色的消息记录列表
 */
export const getMessageList = async (personaId: number | string) => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestGetMethod(`/api/index/messageList?persona_id=${personaId}`, {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request();
    const result = await response.json();

    if (result.code === 200) {
      return result.data;
    } else {
      console.error('获取消息记录失败:', result.msg);
      throw new Error(result.msg || '获取消息记录失败');
    }
  } catch (error) {
    console.error('获取消息记录请求失败:', error);
    throw error;
  }
};

/**
 * 更新用户背景图片
 * @param imagePath 图片路径（需要是已上传的图片路径）
 * @returns 更新结果
 */
export const updateUserBackground = async (imagePath: string) => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestPostMethod('/api/index/imageUpdate', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request({
      image: imagePath,
    });
    const result = await response.json();

    if (result.code === 200) {
      return true;
    } else {
      console.error('更新背景图片失败:', result.msg);
      throw new Error(result.msg || '更新背景图片失败');
    }
  } catch (error) {
    console.error('更新背景图片请求失败:', error);
    throw error;
  }
};

/**
 * 绑定用户的角色
 * @returns 更新结果
 * @param roleId
 */
export const userBindRole = async (roleId: string | number) => {
  // 从localStorage获取token
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('未登录或token不存在');
  }

  const request = requestPostMethod('/api/index/personaBind', {
    baseUrl: BASE_URL,
    headers: {
      token: token,
    },
  });

  try {
    const response = await request({
      id: roleId,
    });
    const result = await response.json();

    if (result.code === 200) {
      return true;
    } else {
      console.error('绑定角色失败:', result.msg);
      throw new Error(result.msg || '绑定角色失败');
    }
  } catch (error) {
    console.error('绑定角色请求失败:', error);
    throw error;
  }
};
